import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl } from '@angular/forms';
import { finalize, forkJoin } from 'rxjs';
import { ActivityService } from '../../../services/activity-services';
import { SpinnerServiceService } from 'src/app/modules/shared/serviceSpinner/spinner-service.service';
import { PresenceDTO } from '../../models/presence.model';
import { TableUtil } from 'src/app/utils/TableExport/TableUtili';
import { BreadcrumbItem } from 'src/app/modules/shared/components/breadcrumb/breadcrumb.component';
import { Activity } from 'src/app/model/Avtivity';
import { ActivityType } from 'src/app/model/activityType';

// ===== REFONTE : écran des confirmations d'une activité =====
// Mêmes appels au back qu'avant ; ce qui change :
// - la liste est chargée une fois, puis filtrée, cherchée et paginée en mémoire ;
// - compteurs (invités, confirmés, présents, sans réponse) et filtres par statut ;
// - présence : un bouton « Marquer présent » (même popup de confirmation qu'avant) ;
// - admin : modifier le téléphone (dans la cellule Téléphone) et retirer la personne ;
// - les erreurs s'affichent dans un bandeau au lieu de laisser le spinner tourner ;
// - l'export reprend la liste filtrée, avec des colonnes lisibles.

/** Une ligne de la liste renvoyée par /activity/listparticipants/{id} */
interface Participant {
  id: string;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  telephone?: string | null;
  phoneNumberOne?: string | null;
  participated?: boolean | null;
  present?: boolean | null;
}

type FiltreStatut = 'tous' | 'confirmes' | 'presents' | 'sansReponse';

/** GET /activity/{id} renvoie l'entité du back : elle contient aussi « country », absent du modèle front */
type ActiviteDetails = Activity & {
  country?: { name?: string | null; flag?: string | null } | null;
};

@Component({
  selector: 'app-activity-participants',
  templateUrl: './activity-participants.component.html',
  styleUrls: ['./activity-participants.component.css'],
})
export class ActivityParticipantsComponent implements OnInit {
  idActivity!: number;
  activity: ActiviteDetails | null = null;
  participants: Participant[] = [];
  chargement = true;

  isAdmin = false;
  iufSupervisor = '';
  breadCrumbItems: BreadcrumbItem[] = [];

  // Recherche, filtre et pagination (en mémoire)
  searchCtrl!: FormControl<string | null>;
  filtre: FiltreStatut = 'tous';
  currentPage = 1;
  readonly itemsPerPage = 20;

  // Bandeaux
  erreur: string | null = null;
  succes: string | null = null;
  private succesTimer?: ReturnType<typeof setTimeout>;

  // Présence
  participantPresence: Participant | null = null;
  presenceEnCours = false;

  // Suppression (admin)
  participantToDelete: Participant | null = null;
  suppressionEnCours = false;

  // Téléphone (admin) : saisie puis confirmation, comme avant
  participantToEditPhone: Participant | null = null;
  newPhoneNumber = '';
  etapeTelephone: 'saisie' | 'confirmation' | null = null;
  telephoneEnCours = false;
  // ===== AJOUT : Activité informelle, le numéro a-t-il déjà bénéficié du dispositif ? =====
  verificationTelephone = false;
  erreurTelephone: string | null = null;
  // ===== FIN AJOUT =====

  constructor(
    private route: ActivatedRoute,
    private activityService: ActivityService,
    private spinnerService: SpinnerServiceService,
    private router: Router,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    const user = this.lireUtilisateur();
    this.iufSupervisor = user?.iuf ?? '';
    this.isAdmin = (user?.roles ?? []).some(
      (role: { roleName: string }) => role.roleName === 'admin',
    );

    this.searchCtrl = this.formBuilder.control('');
    this.searchCtrl.valueChanges.subscribe(() => (this.currentPage = 1));

    this.idActivity = Number(this.route.snapshot.params['id']);
    this.breadCrumbItems = [
      { label: 'OUTILS' },
      { label: 'ACTIVITES', url: '/dashboard/agenda' },
      {
        label: 'CONFIRMATIONS',
        url: '/dashboard/agenda/activity-details/' + this.idActivity,
      },
    ];
    this.charger();
  }

  private lireUtilisateur(): any {
    try {
      return JSON.parse(sessionStorage.getItem('user') ?? '{}');
    } catch {
      return {};
    }
  }

  // ---------------------------------------------------------------- chargement
  private charger(): void {
    this.chargement = true;
    this.spinnerService.showSpinner();
    forkJoin({
      activite: this.activityService.getActivityById(this.idActivity),
      liste: this.activityService.getAllParticipantsOfActivity(this.idActivity),
    })
      .pipe(
        finalize(() => {
          this.chargement = false;
          this.spinnerService.hideSpinner();
        }),
      )
      .subscribe({
        next: ({ activite, liste }) => {
          this.activity = activite;
          this.participants = Array.isArray(liste) ? liste : [];
          this.corrigerPage();
        },
        error: () =>
          this.afficherErreur(
            "Impossible de charger l'activité et ses participants. Recharge la page dans un instant.",
          ),
      });
  }

  /** Recharge seulement la liste (après une suppression ou un changement de téléphone) */
  private rechargerListe(): void {
    this.activityService
      .getAllParticipantsOfActivity(this.idActivity)
      .subscribe({
        next: (liste: Participant[]) => {
          this.participants = Array.isArray(liste) ? liste : [];
          this.corrigerPage();
        },
        error: () =>
          this.afficherErreur(
            "La liste n'a pas pu être rechargée. Recharge la page pour voir la dernière version.",
          ),
      });
  }

  public goBack(): void {
    this.router.navigateByUrl('/dashboard/agenda');
  }

  // ---------------------------------------------------------------- en-tête
  get estInformelle(): boolean {
    return String(this.activity?.['activityType']) === 'COMMERCANT_LIVREUR';
  }

  /** Même libellé que dans la liste déroulante « Type d'activité » */
  get libelleType(): string {
    const valeur = String(this.activity?.['activityType'] ?? '');
    const trouve = (ActivityType.TypesActivity as any[])?.find(
      (t) => t.value === valeur,
    );
    return trouve ? trouve.name : valeur;
  }

  /** Les anciennes activités sans pays sont au Sénégal */
  get libellePays(): string {
    const pays = this.activity?.country;
    if (pays?.name) {
      return pays.name;
    }
    const flag = pays?.flag ?? this.activity?.countryFlag ?? 'sn';
    return String(flag).toLowerCase() === 'bj' ? 'Bénin' : 'Sénégal';
  }

  // ===== AJOUT : drapeau devant le pays =====
  /** "sn" ou "bj" ; les anciennes activités sans pays sont au Sénégal */
  get codePays(): 'sn' | 'bj' {
    const flag =
      this.activity?.country?.flag ?? this.activity?.countryFlag ?? 'sn';
    return String(flag).toLowerCase() === 'bj' ? 'bj' : 'sn';
  }
  // ===== FIN AJOUT =====

  get estFermee(): boolean {
    return this.activity?.['isClosed'] === true;
  }

  /** "06:45:00" → "06:45" */
  get heure(): string {
    const h = this.activity?.['hours'];
    return h ? String(h).slice(0, 5) : '';
  }

  // ---------------------------------------------------------------- compteurs
  get nbInvites(): number {
    return this.participants.length;
  }

  /** Une personne présente compte aussi comme confirmée (comme dans l'ancien écran) */
  get nbConfirmes(): number {
    return this.participants.filter((p) => p.participated || p.present).length;
  }

  get nbPresents(): number {
    return this.participants.filter((p) => p.present === true).length;
  }

  get nbSansReponse(): number {
    return this.participants.filter((p) => !p.participated && !p.present)
      .length;
  }

  pourcentage(n: number): number {
    return this.nbInvites ? Math.round((n * 100) / this.nbInvites) : 0;
  }

  // ---------------------------------------------------------------- statut d'une ligne
  statut(p: Participant): { libelle: string; classe: string } {
    if (p.present) {
      return { libelle: 'Présent', classe: 'cf-statut--present' };
    }
    if (p.participated) {
      return { libelle: 'Confirmé', classe: 'cf-statut--confirme' };
    }
    return { libelle: 'Sans réponse', classe: 'cf-statut--attente' };
  }

  /** Même règle qu'avant : JPO, mini JPO et Activité informelle utilisent « telephone » */
  telephoneDe(p: Participant): string {
    const type = String(this.activity?.['activityType'] ?? '');
    const valeur = ['MINIJPO', 'JPO', 'COMMERCANT_LIVREUR'].includes(type)
      ? p.telephone
      : p.phoneNumberOne;
    return (valeur ?? '').trim();
  }

  nomComplet(p: Participant): string {
    return [p.firstname, p.lastname]
      .map((v) => (v ?? '').trim())
      .filter((v) => v)
      .join(' ');
  }

  // ---------------------------------------------------------------- filtre, recherche, pagination
  choisirFiltre(filtre: FiltreStatut): void {
    this.filtre = filtre;
    this.currentPage = 1;
  }

  get participantsFiltres(): Participant[] {
    const texte = (this.searchCtrl?.value ?? '').trim().toLowerCase();
    return this.participants.filter((p) => {
      if (this.filtre === 'confirmes' && !p.participated && !p.present)
        return false;
      if (this.filtre === 'presents' && !p.present) return false;
      if (this.filtre === 'sansReponse' && (p.participated || p.present))
        return false;
      if (!texte) return true;
      return [
        p.firstname,
        p.lastname,
        this.nomComplet(p),
        p.email,
        p.id,
        p.telephone,
        p.phoneNumberOne,
      ].some((v) => typeof v === 'string' && v.toLowerCase().includes(texte));
    });
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(this.participantsFiltres.length / this.itemsPerPage),
    );
  }

  get pageCourante(): Participant[] {
    const debut = (this.currentPage - 1) * this.itemsPerPage;
    return this.participantsFiltres.slice(debut, debut + this.itemsPerPage);
  }

  get premiereLigne(): number {
    return this.participantsFiltres.length
      ? (this.currentPage - 1) * this.itemsPerPage + 1
      : 0;
  }

  get derniereLigne(): number {
    return Math.min(
      this.currentPage * this.itemsPerPage,
      this.participantsFiltres.length,
    );
  }

  previousPage(): void {
    if (this.currentPage > 1) this.currentPage--;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  /** Après un rechargement, on reste sur la même page si elle existe encore */
  private corrigerPage(): void {
    this.currentPage = Math.min(this.currentPage, this.totalPages);
  }

  // ---------------------------------------------------------------- export
  // ===== MODIF : toutes les colonnes de ParticipantsResponse, avec des titres lisibles =====
  /** Titre de chaque champ de ParticipantsResponse dans l'export, dans l'ordre du DTO */
  private readonly COLONNES_EXPORT: Record<string, string> = {
    id: 'Identifiant',
    firstname: 'Prénom',
    lastname: 'Nom',
    email: 'Email',
    phoneNumberOne: 'Téléphone (profil)',
    participated: 'A confirmé',
    present: 'Présent',
    idSiteJPO: 'Id du site JPO',
    siteJPO: 'Site JPO',
    dateJPO: 'Date JPO',
    telephone: 'Téléphone (inscription)',
    statusPro: 'Statut professionnel',
    secteurActivite: "Secteur d'activité",
    ville: 'Ville',
    region: 'Région',
    date: 'Date',
    age: 'Âge',
    creneau: 'Créneau',
    gender: 'Genre',
  };
  private readonly COLONNES_OUI_NON = ['participated', 'present'];

  /**
   * Exporte les lignes affichées (filtre et recherche compris) avec tous leurs champs.
   * Les colonnes vides pour toutes les lignes sont retirées (ex. : Site JPO pour un Bootcamp).
   */
  exportTable(): void {
    const nomActivite = String(this.activity?.nomActivity ?? '').trim();
    const participants = this.participantsFiltres;
    const brut = (p: Participant) => p as unknown as Record<string, unknown>;

    // Champs connus dans l'ordre du DTO, puis un éventuel champ ajouté plus tard côté back
    const champs = Object.keys(this.COLONNES_EXPORT);
    participants.forEach((p) =>
      Object.keys(brut(p)).forEach((cle) => {
        if (!champs.includes(cle)) champs.push(cle);
      }),
    );
    const estVide = (v: unknown) =>
      v === null || v === undefined || String(v).trim() === '';
    const champsUtiles = champs.filter(
      (cle) =>
        this.COLONNES_OUI_NON.includes(cle) ||
        participants.some((p) => !estVide(brut(p)[cle])),
    );

    const lignes = participants.map((p) => {
      const ligne: Record<string, unknown> = {};
      champsUtiles.forEach((cle) => {
        const titre = this.COLONNES_EXPORT[cle] ?? cle;
        const valeur = brut(p)[cle];
        ligne[titre] = this.COLONNES_OUI_NON.includes(cle)
          ? valeur
            ? 'Oui'
            : 'Non'
          : estVide(valeur)
            ? ''
            : valeur;
      });
      ligne['Statut'] = this.statut(p).libelle;
      return ligne;
    });
    TableUtil.exportArrayToExcel(lignes, this.nomExport(nomActivite));
  }

  /**
   * Nom de la feuille et du fichier : Excel refuse les caractères : \ / ? * [ ] dans un nom de feuille,
   * et le limite à 31 caractères.
   */
  private nomExport(nom: string): string {
    const propre = nom
      .replace(/[:\\/?*\[\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 31)
      .trim();
    return propre || 'Participants';
  }
  // ===== FIN MODIF =====

  // ---------------------------------------------------------------- présence
  ouvrirPresence(p: Participant): void {
    if (p.present) return;
    this.participantPresence = p;
  }

  fermerPresence(): void {
    if (!this.presenceEnCours) this.participantPresence = null;
  }

  confirmerPresence(): void {
    const p = this.participantPresence;
    if (!p || !this.activity || this.presenceEnCours) return;
    this.presenceEnCours = true;
    const presenceInfo = new PresenceDTO(
      p.id,
      this.iufSupervisor,
      this.idActivity, // l'id de la route : c'est la même activité, et il est toujours renseigné
    );
    this.activityService
      .setParticipantPresence(presenceInfo)
      .pipe(finalize(() => (this.presenceEnCours = false)))
      .subscribe({
        next: () => {
          // Comme avant : une personne présente compte aussi comme confirmée
          p.present = true;
          p.participated = true;
          this.participantPresence = null;
          this.afficherSucces(
            `${this.nomComplet(p) || p.id} est marqué(e) présent(e).`,
          );
        },
        error: (err) => {
          this.participantPresence = null;
          this.afficherErreur(
            err?.error?.message ??
              "La présence n'a pas pu être enregistrée. Réessaie dans un instant.",
          );
        },
      });
  }

  // ---------------------------------------------------------------- suppression (admin)
  ouvrirSuppression(p: Participant): void {
    this.participantToDelete = p;
  }

  closeDeleteModal(): void {
    if (!this.suppressionEnCours) this.participantToDelete = null;
  }

  deleteParticipant(): void {
    const p = this.participantToDelete;
    if (!p || this.suppressionEnCours) return;
    this.suppressionEnCours = true;
    this.activityService
      .removeParticipant(this.idActivity, p.id)
      .pipe(finalize(() => (this.suppressionEnCours = false)))
      .subscribe({
        next: () => {
          this.participantToDelete = null;
          this.afficherSucces(
            `${this.nomComplet(p) || p.id} a été retiré(e) de l'activité.`,
          );
          this.rechargerListe();
        },
        error: (err) => {
          this.participantToDelete = null;
          this.afficherErreur(
            err?.error?.message ??
              "La personne n'a pas pu être retirée. Réessaie dans un instant.",
          );
        },
      });
  }

  // ---------------------------------------------------------------- téléphone (admin)
  ouvrirTelephone(p: Participant): void {
    this.participantToEditPhone = p;
    this.newPhoneNumber = this.telephoneDe(p);
    this.erreurTelephone = null;
    this.etapeTelephone = 'saisie';
  }

  // ===== MODIF : pour une Activité informelle, même contrôle que le formulaire d'inscription =====
  /** Le message d'erreur disparaît dès qu'on retape le numéro */
  numeroModifie(): void {
    this.erreurTelephone = null;
  }

  verifierTelephone(): void {
    const p = this.participantToEditPhone;
    const numero = this.newPhoneNumber.trim();
    if (!p || !numero || this.verificationTelephone) return;
    this.erreurTelephone = null;

    if (numero === this.telephoneDe(p)) {
      this.erreurTelephone = "C'est déjà le numéro de cette personne.";
      return;
    }
    // Les autres types n'ont pas cette règle : on passe directement à la confirmation
    if (!this.estInformelle) {
      this.etapeTelephone = 'confirmation';
      return;
    }

    // ===== AJOUT : numéro Wave = chiffres seulement, comme dans le formulaire d'inscription =====
    // (un « + » ou des espaces fausseraient la recherche et le numéro enregistré)
    const chiffres = numero.replace(/[\s.\-]/g, '');
    if (!/^\d+$/.test(chiffres)) {
      this.erreurTelephone =
        'Saisis uniquement les chiffres du numéro Wave, sans indicatif (ex : 770000000).';
      return;
    }
    this.newPhoneNumber = chiffres;
    if (chiffres === this.telephoneDe(p)) {
      this.erreurTelephone = "C'est déjà le numéro de cette personne.";
      return;
    }
    // ===== FIN AJOUT =====

    this.verificationTelephone = true;
    this.activityService
      .verifierNumeroWave(chiffres) // ← endpoint authentifié (controller activity)
      .pipe(finalize(() => (this.verificationTelephone = false)))
      .subscribe({
        next: (existe: boolean) => {
          if (existe) {
            this.erreurTelephone =
              'Ce numéro Wave a déjà bénéficié des activités informelles.';
          } else {
            this.etapeTelephone = 'confirmation';
          }
        },
        // Contrairement au formulaire public, on ne laisse pas passer sans vérification
        error: () => {
          this.erreurTelephone =
            "Impossible de vérifier ce numéro pour l'instant. Réessaie dans un instant.";
        },
      });
  }
  // ===== FIN MODIF =====

  /** Fermeture demandée par l'utilisateur : bloquée pendant l'enregistrement */
  fermerTelephone(): void {
    if (this.telephoneEnCours) return;
    this.reinitialiserTelephone();
  }

  // ===== CORRECTIF : la popup restait ouverte après l'enregistrement =====
  // finalize() ne s'exécute qu'APRÈS next/error : telephoneEnCours valait encore true,
  // donc fermerTelephone() sortait tout de suite. On ferme ici sans cette garde.
  private reinitialiserTelephone(): void {
    this.erreurTelephone = null; // ← AJOUT
    this.etapeTelephone = null;
    this.participantToEditPhone = null;
    this.newPhoneNumber = '';
  }
  // ===== FIN CORRECTIF =====

  confirmUpdatePhone(): void {
    const p = this.participantToEditPhone;
    const numero = this.newPhoneNumber.trim();
    if (!p || !numero || this.telephoneEnCours) return;
    this.telephoneEnCours = true;
    this.activityService
      .updateParticipantPhone(this.idActivity, p.id, numero)
      .pipe(finalize(() => (this.telephoneEnCours = false)))
      .subscribe({
        next: () => {
          this.reinitialiserTelephone(); // ← CORRECTIF
          this.afficherSucces(
            `Le numéro de ${this.nomComplet(p) || p.id} a été mis à jour.`,
          );
          this.rechargerListe();
        },
        error: (err) => {
          this.reinitialiserTelephone(); // ← CORRECTIF
          this.afficherErreur(
            err?.error?.message ??
              "Le numéro n'a pas pu être modifié. Réessaie dans un instant.",
          );
        },
      });
  }

  // ---------------------------------------------------------------- bandeaux et clavier
  afficherErreur(message: string): void {
    this.succes = null;
    this.erreur = message;
  }

  afficherSucces(message: string): void {
    this.erreur = null;
    this.succes = message;
    clearTimeout(this.succesTimer);
    this.succesTimer = setTimeout(() => (this.succes = null), 5000);
  }

  @HostListener('document:keydown.escape')
  fermerAvecEchap(): void {
    if (this.etapeTelephone) this.fermerTelephone();
    else if (this.participantToDelete) this.closeDeleteModal();
    else if (this.participantPresence) this.fermerPresence();
  }
}
// ===== FIN REFONTE =====
